// `DataTable` (design-system spec §3.3) — the one way to render tabular data
// in migrated screens, and the register's centerpiece. Dense 12.5 px cells,
// uppercase 11 px headers on the well tone, right-aligned tabular numerics,
// inline row actions as accent links, a pagination footer, and the phone
// collapse contract BUILT IN: below the collapse width the header row drops,
// every row becomes a labelled card, and every non-leading cell carries
// `data-label` from `column.label ?? header` — the geometry the responsive
// nets assert stays a property of the primitive, not of each screen.
//
// WAVE 1 (P3-C-0, the CRM benchmark program — D-241/D-242): four
// OPT-IN additions on the expansion-prop precedent below — every caller that
// passes none of them compiles and renders byte-identically:
//
//   1a `sticky`      — sticky header + frozen first column. Pure CSS: the
//                      wrapper becomes the one bounded scroll region
//                      (`.ds-tbl-wrap--pane`) and both halves fall out of
//                      `position: sticky` against it. ALL of that CSS gates
//                      behind `min-width: 769px` — the exact complement of the
//                      collapse block — so the phone card contract is
//                      untouched by construction.
//   1b `sort` + per-column `sortKey` — server-side sort (D-242). The column
//                      states the endpoint's order-enum BASE key (an explicit
//                      wire token, never `sortable: true`, because column.key
//                      and wire key diverge); the screen owns the state,
//                      URL-serialized like filters. One caret ever; `aria-sort`
//                      on the active th only; no third "unsorted" state — the
//                      endpoint always has an order.
//   1c `selectable` + `bulkBar` — multi-select folded in from the
//                      quarantine-board pattern. ONE internal leading column
//                      (key "_sel"); select-all covers ONLY the loaded rows,
//                      never a fake "select all N matching". Clearing the
//                      selection on filter/sort/page change is the SCREEN's
//                      contract (the same effect that resets its cursor stack
//                      — the primitive cannot see the query); the wave-1
//                      reference tests pin the idiom.
//   1d `keyboardNav` — roving tabindex on body rows; arrows move the anchor,
//                      Enter opens, Space selects. ROW-level only — cell-grid
//                      navigation is a D-241 kill (pilot-pull trigger).
//
// THE ONE COLLAPSE-CONTRACT CHANGE (1c): the selection checkbox becomes DOM
// cell index 0, so the card-title cell moves to index 1 and the data-label
// rule becomes "every cell after the first NON-SELECTION cell" — the card
// subject stays unlabelled, the control-only checkbox cell carries no text for
// the net to flag, and `e2e/mobile-layout.ts` states the matching exemption
// ("the first cell carrying text is the title") with its reasoning.
//
// COLUMN CONFIGURATION (PR-E4, D-243 ruling 3, D-305 rulings 8-10 and 22,
// design-system §3.3). Three more opt-in inputs, on the same rule: a caller
// that passes none of them renders byte-identically, and
// `data-table-columns.test.tsx` pins that against markup captured from the
// primitive before this change.
//
//   `presence` (column) — the redaction fence. A column without it is
//                      structural and exists on every page. A column with it
//                      exists only when the predicate holds for the loaded
//                      rows, whatever a stored view says, and never on an
//                      empty page. The test is never `key in row`: that hides
//                      derived columns and shows marker-redacted ones.
//   `defaultHidden` (column) — renders only when a config shows it.
//   `columnConfig` (table) — the screen's ordered `{key, hidden?, width?}`
//                      entries. `resolveColumns` applies them with the pins:
//                      the first declared column is the lead (the identifier
//                      and the phone card's subject), the `actions` column is
//                      the trail, and neither can be hidden or moved. A stored
//                      entry can never throw (a shared view reaches every
//                      reader of the screen); only the screen's own
//                      declarations can.
//
// Widths render a `<colgroup>` and `ds-tbl--sized` only when some column has
// one, and every rule that gives them effect, the resize separator's included,
// sits inside `min-width: 769px`, so a phone renders no widths. Everything is
// decided in the one render from props, with no measuring effect, so a screen
// that reveals the table atomically (§5) never sees it move afterwards.
//
// THE PEEK (P3-E2 PR E2-1, D-314, design-system §3.3 and §3.12). One more
// opt-in input on the same rule, and `data-table-peek.test.tsx` pins a table
// without it against markup captured before it existed. `peek` renders a
// labelled Peek button after the lead cell's content. The button carries
// `data-peek-key` (the address form, `<kind>:<uuid>`, which is how `SidePeek`
// finds it to give focus back) and `aria-expanded`, and on the peeked row it
// names the panel in `aria-controls` while the row takes `is-peeked`. Pressing
// it opens the record beside the list without navigating: its click never
// reaches the row, so a row click keeps its own meaning. The peeked row's
// button closes the peek. With `keyboardNav`, Shift+Enter on a row does what
// its button does and plain Enter still opens the row. Below 769 px no button
// renders and Shift+Enter falls through to the row, because no list offers a
// peek on a phone (D-314 ruling 4).
//
// FIT, WRAP BEFORE WIDEN (P3-E2 PR E2-1, design-system §3.3). One more opt-in
// input on the same rule: a table without `fit` renders as it did, byte for
// byte, and `data-table-fit.test.tsx` pins that. `fit` adds `ds-tbl--fit`,
// and from 769 px up that class lets every header label and every chip in a
// cell take a second line, the geometry a sized table's headers and the
// §3.5b AR chip already have. A table sizes each column no narrower than its
// widest unbreakable run, so under `nowrap` that run is a whole label or a
// whole chip sentence, and with `fit` it is one word. It exists because the
// customer book gained its Next step column and its Peek button in E2-1 and
// its Russian labels («Платёжная дисциплина», «Дебиторская задолженность»)
// and chips («Встреча · Просрочено с 28 июн. 2026 г.») then held it 1455 px
// wide at a 1440 px viewport, with the trailing Created sort control off the
// glass (the W-SWEEP locale pass). Nothing is hidden and no column is
// dropped: a long label or chip reads on two lines instead of pushing the
// last column off the screen.
//
// EDITABLE, ONE FIELD IN PLACE (P3-E2 PR E2-2d, D-326, design-system §3.3 and
// §3.13). One more opt-in input, per column, on the same rule: a column
// without `editable` renders as it did, byte for byte, and
// `cell-editor.test.tsx` pins that. A column with it renders the
// `CellEditor` (`cell-editor.tsx`) in its cell from 769 px up, and below that
// its value alone, drawn by `editable.display`, because a phone edits through
// the record's own page. Its value is drawn by `display` at every width, so it
// declares no `render` (runtime-guarded). The editor's edit button is the only
// way in and its click never reaches the row, so a row click keeps its
// meaning; the row keys (`keyboardNav`) are unchanged and no cell grid is added
// (D-326 ruling 2). A column whose `presence` is false renders no cell, so it
// renders no editor.

import { Fragment, useId, useRef, useState } from "react";
import type { CSSProperties, HTMLAttributes, KeyboardEvent, PointerEvent, ReactNode } from "react";
import { CellEditor, cellEditableValue } from "./cell-editor";
import type { CellEditable } from "./cell-editor";
import { SIDE_PEEK_ID } from "./peek-address";
import { TableSkeleton } from "./states";
import { useDesktop } from "./use-desktop";

/** Extra attributes a screen may put on a row: drag handlers, `data-*` keys. */
export type DataTableRowProps = Omit<HTMLAttributes<HTMLTableRowElement>, "className"> &
  Record<`data-${string}`, string | number | boolean | undefined>;

/** The internal selection column's key — reserved (runtime-guarded). */
const SEL_KEY = "_sel";

/** Rev-7 column kinds (design-system §3.17). */
export type DataTableColumnKind = "id" | "date" | "money" | "qty" | "status";

/** How many prioritised columns a phone card shows (rev-7). */
export const PHONE_CARD_FIELDS = 3;

/**
 * True for cell content that shows nothing a reader could use: nothing at
 * all, an empty string, or a lone dash. A phone card skips such a cell rather
 * than printing "TAGS —" and "OWNER —" down the card (rev-7). Elements are
 * never judged empty, because the primitive cannot see what they render.
 */
export function isEmptyCellContent(content: ReactNode): boolean {
  if (content === null || content === undefined || content === false || content === true) {
    return true;
  }
  if (typeof content === "string") return /^\s*[—–-]?\s*$/u.test(content);
  return false;
}

/**
 * The keys a phone card hides when the table prioritises its columns (rev-7):
 * every column without a priority and every prioritised one past the first
 * three, except the card's subject, its `status` cells and its actions.
 * Undefined when no column declares a priority, which shows every column.
 */
export function phoneHiddenKeys<Row, K extends string>(
  columns: readonly DataTableColumn<Row, K>[],
): ReadonlySet<string> | undefined {
  if (!columns.some((c) => c.phonePriority !== undefined)) return undefined;
  const lead = columns[0];
  const kept = columns
    .filter((c) => c.phonePriority !== undefined && c !== lead)
    .sort((a, b) => (a.phonePriority ?? 0) - (b.phonePriority ?? 0))
    .slice(0, PHONE_CARD_FIELDS)
    .map((c) => c.key);
  const keep = new Set(kept);
  return new Set(
    columns
      .filter((c) => c !== lead && c.kind !== "status" && c.actions !== true && !keep.has(c.key))
      .map((c) => c.key),
  );
}

/** The cell classes a rev-7 kind adds, beside the classes it implies. */
function kindClasses(kind: DataTableColumnKind | undefined): string[] {
  switch (kind) {
    case "id":
      return ["ds-td-id", "ds-td-k-id"];
    case "money":
      return ["ds-td-num", "ds-td-k-money"];
    case "qty":
      return ["ds-td-num", "ds-td-k-qty"];
    case "date":
      return ["ds-td-k-date"];
    case "status":
      return ["ds-td-k-status"];
    default:
      return [];
  }
}

/** True when a click landed on a control inside the row, which keeps its own
 * meaning and never opens the row's peek. */
function fromControl(target: EventTarget | null, row: Element): boolean {
  if (!(target instanceof Element)) return false;
  const control = target.closest("a, button, input, select, textarea, label, [role='button']");
  return control !== null && control !== row && row.contains(control);
}

export interface DataTableColumn<Row, K extends string = string> {
  key: string;
  /** Header cell content (plain string doubles as the collapse label). */
  header: string;
  /** Rendered header cell when the header is a CONTROL rather than a word —
   * the bulk-select column's select-all checkbox (§3.3). `header` remains the
   * accessible/collapse label, so the phone card still names the cell.
   * Mutually exclusive with `sortKey` (runtime-guarded): a control header
   * cannot also be a sort toggle. */
  headerNode?: ReactNode;
  /** WAVE 1b (D-242): the endpoint's order-enum BASE key (the asc token),
   * typed per screen from the generated api-client enum (D-142). Presence
   * marks the column sortable. An explicit wire token, not `sortable: true`,
   * because `column.key` and the wire key diverge (the customers screen's
   * `owner_user_id` column has no honest sort). */
  sortKey?: K;
  /** First-activation direction when this column is not the active sort
   * (default "asc"; screens pass "desc" for date/score keys). */
  sortFirst?: "asc" | "desc";
  render?: (row: Row) => ReactNode;
  /** Right-aligned tabular numerals. */
  numeric?: boolean;
  /** Right-aligned inline-action cell ("Fix rows", "Review", "Bind"). */
  actions?: boolean;
  /** Identifier column — 600 weight ink. */
  id?: boolean;
  /** Muted tabular age/meta column. */
  age?: boolean;
  /** Collapse label override when `header` is not it. */
  label?: string;
  /**
   * Rev-7 (D-426): what the column holds. From 769 px up each kind keeps its
   * value on one line in tabular figures, so "WO-2025-00001", "Oct 28, 2026"
   * and "KZT 100,000,000.00" never break mid-token. `money` and `qty` are
   * also right-aligned (they imply `numeric`), `id` is also the 600-weight
   * identifier (it implies `id`), and `status` is a chip cell. A `fit` table
   * keeps its own wrapping rules and ignores the one-line half.
   */
  kind?: DataTableColumnKind | undefined;
  /**
   * Rev-7: the column's place on a phone card, 1 first. When any column of a
   * table declares one, a card shows its subject, its `status` cells, its
   * actions and the three lowest-numbered columns, and leaves the rest to the
   * record's own page. A table where no column declares one shows every
   * column, as before. On every table a phone card skips a cell with no value.
   */
  phonePriority?: number | undefined;
  /**
   * PR-E4 (D-305 ruling 8): the redaction fence. Absent, the column is
   * structural. Present, the column exists on the page only when this holds
   * for the loaded rows, and it is never called on an empty page, where a
   * redactable column is absent. The screen states its module's evidence:
   * omission is `rows.some((r) => r.total_minor !== undefined)`, a derived
   * column is the mirror check AND a row carrying the accumulator, and marker
   * redaction is `costPresence(rows, valueOf) === "visible"`. Never
   * `key in row`.
   */
  presence?: (rows: readonly Row[]) => boolean;
  /** PR-E4: rendered only when a column configuration shows it
   * (`hidden: false`). Ignored on a pinned column, which is always shown. */
  defaultHidden?: boolean;
  /**
   * P3-E2 PR E2-2d (D-326): the §3.13 cell editor in this column's cells, one
   * of the kinds `select`, `user`, `tags` and `toggle`. The caller passes the
   * options, the per-row writability and its reason, every label and the one
   * function that sends the single-field write; the primitive holds no string
   * and no route. The value is drawn by `editable.display`, so a column with
   * `editable` declares no `render`. Below 769 px the cell shows the value
   * alone.
   */
  editable?: CellEditable<Row> | undefined;
}

/** One entry of a column configuration (spec 16 §3.12 item 2): position in
 * the list is the column's order, an absent `hidden` means the column's
 * default, and `width` is whole pixels from 64 to 640. */
export interface ColumnConfigEntry {
  key: string;
  hidden?: boolean | undefined;
  width?: number | undefined;
}

/** PR-E4 — the table's column configuration. */
export interface DataTableColumnConfig {
  /** The screen's current entries, ordered. `[]` leaves every column at its
   * default. A stored view's entries go here as they are; `resolveColumns`
   * ignores what does not apply to this page and never throws on them. */
  value: readonly ColumnConfigEntry[];
  /** Receives a resize: the column's key and its new width, already clamped,
   * or `undefined` when Home resets it. Without it no separator renders, since
   * a separator that changes nothing would be a false offer to the keyboard. */
  onResize?: (key: string, width: number | undefined) => void;
  /** The separator's accessible name for a column, translated by the CALLER
   * (the primitive stays i18n-free, like `sort.toggleLabel`). */
  resizeLabel: (header: string) => string;
}

/** A column as the configuration places it on this page. */
export interface ResolvedColumn<Row, K extends string = string> {
  readonly key: string;
  readonly column: DataTableColumn<Row, K>;
  /** `lead` is the first declared column, `trail` the declared-last `actions`
   * column. A pinned column keeps its place and cannot be hidden. */
  readonly pin: "lead" | "trail" | undefined;
  readonly hidden: boolean;
  readonly width: number | undefined;
}

// The three contract limits (`SAVED_VIEW_COLUMN_WIDTH_MIN`, `_MAX` and
// `SAVED_VIEW_MAX_COLUMNS` in packages/contracts/src/saved-views/schemas.ts)
// are restated here rather than imported: the contracts root pulls zod and the
// OpenAPI registry into the office bundle, which D-096 ruling 4 rules out for
// constants, and the saved-views contract has no zero-import subpath yet.
// `data-table-columns.test.tsx` pins all three equal to the contract's.
export const COLUMN_WIDTH_MIN = 64;
export const COLUMN_WIDTH_MAX = 640;
/** The most entries a saved column configuration may hold (the contract's
 * `SavedViewColumns.max(40)`); `mergeColumnConfig` never returns more. */
export const COLUMN_CONFIG_MAX = 40;
/** ArrowLeft and ArrowRight move a separator by this many pixels. */
export const COLUMN_RESIZE_STEP = 16;

/** Holds a width to whole pixels from 64 to 640 (spec 16 §3.12 item 2). */
export function clampWidth(px: number): number {
  if (Number.isNaN(px)) return COLUMN_WIDTH_MIN;
  return Math.min(COLUMN_WIDTH_MAX, Math.max(COLUMN_WIDTH_MIN, Math.round(px)));
}

/** First entry per key, and never one naming the internal selection column. */
function uniqueEntries(entries: readonly ColumnConfigEntry[]): ColumnConfigEntry[] {
  const seen = new Set<string>();
  return entries.filter((entry) => {
    if (entry.key === SEL_KEY || seen.has(entry.key)) return false;
    seen.add(entry.key);
    return true;
  });
}

/**
 * Every column PRESENT on this page, in configured order with the pins
 * applied, hidden ones included: what a column chooser lists (its
 * configurable rows are the ones with no `pin`). `resolveColumns` is this
 * less the hidden columns.
 *
 * The rules, each silent, because a stored config reaches every reader of a
 * shared view and must never break the list for any of them:
 * - a column whose `presence` is false, or which declares one on an empty
 *   page, is absent whatever an entry says (the redaction fence);
 * - an entry naming no declared column is ignored here (a mergeColumnConfig
 *   keeps it on save), an entry naming `_sel` is ignored, and of two entries
 *   naming one key the first is kept;
 * - a pinned column's entry keeps its width and loses its `hidden` and its
 *   position;
 * - configured columns take, in configured order, the places they hold in the
 *   declared order, and a column no entry names keeps its declared place, so
 *   `[{key, hidden: false}]` for a column shown by default renders as `[]`.
 */
export function presentColumns<Row, K extends string = string>(
  columns: readonly DataTableColumn<Row, K>[],
  rows: readonly Row[],
  config: readonly ColumnConfigEntry[],
): ResolvedColumn<Row, K>[] {
  const entries = new Map<string, ColumnConfigEntry>();
  for (const entry of uniqueEntries(config)) entries.set(entry.key, entry);

  const lead = columns[0];
  const last = columns[columns.length - 1];
  const trail = last !== undefined && last !== lead && last.actions === true ? last : undefined;
  const isPresent = (column: DataTableColumn<Row, K>): boolean =>
    column.presence === undefined || (rows.length > 0 && column.presence(rows));
  const widthOf = (column: DataTableColumn<Row, K>): number | undefined => {
    const width = entries.get(column.key)?.width;
    return width !== undefined && Number.isFinite(width) ? clampWidth(width) : undefined;
  };

  const middle = columns.filter((c) => c !== lead && c !== trail && isPresent(c));
  const byKey = new Map(middle.map((c) => [c.key, c] as const));
  const configured = [...entries.keys()].flatMap((key) => {
    const column = byKey.get(key);
    return column === undefined ? [] : [column];
  });
  let next = 0;
  const ordered = middle.map((column) => {
    if (!entries.has(column.key)) return column;
    const placed = configured[next] ?? column;
    next += 1;
    return placed;
  });

  const out: ResolvedColumn<Row, K>[] = [];
  if (lead !== undefined && isPresent(lead)) {
    out.push({ key: lead.key, column: lead, pin: "lead", hidden: false, width: widthOf(lead) });
  }
  for (const column of ordered) {
    out.push({
      key: column.key,
      column,
      pin: undefined,
      hidden: entries.get(column.key)?.hidden ?? column.defaultHidden === true,
      width: widthOf(column),
    });
  }
  if (trail !== undefined && isPresent(trail)) {
    out.push({ key: trail.key, column: trail, pin: "trail", hidden: false, width: widthOf(trail) });
  }
  return out;
}

/** The columns the table renders for these rows under this configuration
 * (design-system §3.3): the present, shown columns in configured order with
 * the pins applied and each one's width. */
export function resolveColumns<Row, K extends string = string>(
  columns: readonly DataTableColumn<Row, K>[],
  rows: readonly Row[],
  config: readonly ColumnConfigEntry[],
): ResolvedColumn<Row, K>[] {
  return presentColumns(columns, rows, config).filter((c) => !c.hidden);
}

/**
 * Two configurations are equal when they render the same keys in the same
 * order with the same widths for the same rows, and never entry by entry
 * (spec 16 §3.12 item 5): `[{key, hidden: false}]` for a column shown by
 * default equals `[]`, and a stale or fenced key never makes two differ.
 */
export function sameColumnLayout<Row, K extends string = string>(
  columns: readonly DataTableColumn<Row, K>[],
  rows: readonly Row[],
  a: readonly ColumnConfigEntry[],
  b: readonly ColumnConfigEntry[],
): boolean {
  const left = resolveColumns(columns, rows, a);
  const right = resolveColumns(columns, rows, b);
  return (
    left.length === right.length &&
    left.every((c, i) => {
      const other = right[i];
      return c.key === other?.key && c.width === other.width;
    })
  );
}

/**
 * The entries to save when `edits` are the reader's entries for the columns
 * on this page. Every edit is kept, in its order, and so is every stored entry
 * for a key the edits do not name, in its place: a reader who cannot see a
 * column (a fenced one) or a screen that no longer has it (a stale one) never
 * erases it from the view. Duplicates keep their first entry and `_sel` is
 * dropped, so the result is always one the server accepts by key.
 *
 * The result holds at most `COLUMN_CONFIG_MAX` entries, the contract's cap:
 * the server checks a key by its grammar alone, so a shared view can carry up
 * to 40 keys this screen does not declare, and saving from it with the
 * screen's own columns appended would answer 400. Every edit is kept first;
 * stored entries for keys the edits do not name fill the room that is left,
 * in their order, and the ones past it are dropped from the tail. Only a
 * screen declaring more columns than the cap could lose an edit, and then
 * from the tail too.
 */
export function mergeColumnConfig(
  stored: readonly ColumnConfigEntry[],
  edits: readonly ColumnConfigEntry[],
): ColumnConfigEntry[] {
  const edited = uniqueEntries(edits);
  const editedKeys = new Set(edited.map((e) => e.key));
  const out: ColumnConfigEntry[] = [];
  let room = Math.max(0, COLUMN_CONFIG_MAX - edited.length);
  let next = 0;
  for (const entry of uniqueEntries(stored)) {
    if (!editedKeys.has(entry.key)) {
      if (room > 0) {
        out.push(entry);
        room -= 1;
      }
      continue;
    }
    const placed = edited[next];
    next += 1;
    if (placed !== undefined) out.push(placed);
  }
  out.push(...edited.slice(next));
  return out.slice(0, COLUMN_CONFIG_MAX);
}

export interface DataTablePager {
  /** "1–9 of 9" style range line, already translated. */
  range: string;
  /** Optional "Refreshed 2 min ago" meta, already translated. */
  meta?: string | undefined;
  page: number;
  /** Total pages — omit for CURSOR paging, which cannot know the total. */
  pageCount?: number | undefined;
  onPage: (page: number) => void;
  prevLabel: string;
  nextLabel: string;
  /** Cursor paging: explicit availability of the neighbouring pages. When
   * `pageCount` is given these default to the page-number arithmetic. */
  hasPrev?: boolean | undefined;
  hasNext?: boolean | undefined;
}

/** WAVE 1b — the sort state, owned and URL-serialized by the SCREEN
 * (validateSearch/useSearch, the movements-ledger idiom), validated against
 * the generated enum; absent = the endpoint's declared default. */
export interface DataTableSort<K extends string> {
  /** The current order token: `key` ascending, `-key` descending. */
  value: K | `-${K}`;
  /** Receives the next token. Screens reset their cursor stack AND clear any
   * selection here — a changed sort invalidates the cursor (D-242). */
  onChange: (order: K | `-${K}`) => void;
  /** Translated by the CALLER (the primitive stays i18n-free, like
   * prevLabel/nextLabel): the toggle's accessible sentence given the header
   * and the direction a click yields. */
  toggleLabel: (header: string, next: "ascending" | "descending") => string;
}

/** WAVE 1c — multi-select, folded in from the quarantine-board pattern.
 * `selected` holds `rowKey` values; the primitive never selects a row it has
 * not loaded. */
export interface DataTableSelectable<Row> {
  selected: ReadonlySet<string>;
  onChange: (next: ReadonlySet<string>) => void;
  /** Accessible label of the select-all checkbox, translated. */
  headerLabel: string;
  /** Accessible label of one row's checkbox, translated. */
  rowLabel: (row: Row) => string;
  /** Rows this returns false for render a disabled checkbox and are excluded
   * from select-all. */
  isRowSelectable?: (row: Row) => boolean;
  /** `data-testid` of the row checkboxes (`{testId}-all` on the header one). */
  testId?: string;
}

/** WAVE 1c — the bulk bar, rendered above the table only while the selection
 * is non-empty. All strings are screen-formatted i18n. */
export interface DataTableBulkBar {
  /** "3 selected" — the screen formats the plural. */
  countLabel: string;
  clearLabel: string;
  onClear: () => void;
  /** The verbs. */
  children: ReactNode;
}

/** P3-E2 PR E2-1 (D-314) — the Peek button on each row. Every word arrives
 * translated from the caller; the primitive stays i18n-free. */
export interface DataTablePeek<Row> {
  /** The row's peek in its address form, `<kind>:<uuid>` (`peekCode` in
   * `peek-address.ts`), or undefined when this row offers none: a screen
   * withholds the peek from a row here, for instance a record its reader
   * cannot open. */
  key: (row: Row) => string | undefined;
  /** The peek the address holds now (`usePeekAddress().code`), which marks its
   * row, or undefined when none is open. */
  current: string | undefined;
  /** Opens the row's peek, or switches the open one to it. */
  onOpen: (code: string, row: Row) => void;
  /** Closes the peek when its own row's button is pressed. Without it that
   * press does nothing. */
  onClose?: (() => void) | undefined;
  /** The button's visible word ("Peek"). */
  label: string;
  /** The button's accessible name for a row ("Peek Q-0012"). Without it the
   * name is `label`. */
  rowLabel?: ((row: Row) => string) | undefined;
  /** `data-testid` of the buttons. Defaults to `ds-peek`. */
  testId?: string | undefined;
}

export interface DataTableProps<Row, K extends string = string> {
  columns: readonly DataTableColumn<Row, K>[];
  rows: readonly Row[];
  rowKey: (row: Row) => string;
  empty: ReactNode;
  error?: ReactNode;
  loading?: boolean;
  loadingLabel?: string;
  pager?: DataTablePager | undefined;
  caption?: string;
  onRowClick?: (row: Row) => void;
  /** `data-testid` stamped on every `<tr>`. Defaults to the register's
   * `ds-row`; screens whose certified walkthroughs already key on a row
   * handle (`product-row`, `cost-run-row`, `step-row`) pass theirs. */
  rowTestId?: string;
  /** Extra class on a row (drag state, diff state). */
  rowClassName?: (row: Row) => string | undefined;
  /** Extra attributes on a row (`draggable`, drag handlers, `data-step-no`). */
  rowProps?: (row: Row, index: number) => DataTableRowProps;
  /**
   * PER-ROW EXPANSION PANEL (added UX-3, additive — spec §3: "props may grow").
   *
   * Return content and the primitive emits ONE extra full-width `<tr>` directly
   * beneath that record's row, carrying a single `<td colSpan={columns.length}>`.
   * Return `undefined`/`null` — and, crucially, OMIT THE PROP — and the output
   * is unchanged: exactly one `<tr>` per record, which is the contract every
   * other caller was written against and which `rowTestId`-keyed walkthroughs
   * count on.
   *
   * It exists because two module-04 panels are row-attached FORMS, not
   * decoration: S2's Operations tab opens the skip / record-result / QC-hold
   * form under the operation it acts on, and the Materials tab opens the return
   * form under the issue it reverses. The form's meaning is "this row" — with
   * eight operations listed, a form floating below the table is a form whose
   * subject the reader has to reconstruct. `colSpan` detail rows are the
   * standard table affordance for exactly that, and the certified walkthroughs
   * address the form while its row is on screen, so lifting it out of the table
   * would move DOM that "restyling never rewrites a walkthrough" (§6a) protects.
   */
  expansion?: (row: Row) => ReactNode;
  /** Collapse label for the expansion cell — the phone card names the panel the
   * way `column.label ?? header` names every other cell. */
  expansionLabel?: (row: Row) => string | undefined;
  /** WAVE 1b — see `DataTableSort`. Columns without `sortKey` are untouched;
   * without this prop no column renders a toggle. */
  sort?: DataTableSort<K>;
  /** WAVE 1c — see `DataTableSelectable`. */
  selectable?: DataTableSelectable<Row>;
  /** WAVE 1c — see `DataTableBulkBar`. Requires `selectable`. */
  bulkBar?: DataTableBulkBar;
  /** WAVE 1d — roving tabindex + arrows/Enter/Space on body rows (opt-in). */
  keyboardNav?: boolean;
  /** WAVE 1a — sticky header + frozen first column at desktop widths. The
   * wrapper becomes the bounded scroll region; screens tune the viewport
   * budget via `--tbl-pane-offset` (default 260px, styles/office.css). */
  sticky?: boolean;
  /** PR-E4 — see `DataTableColumnConfig`. Passing it pins the lead and the
   * `actions` trail and arms the guard on the screen's own declarations. */
  columnConfig?: DataTableColumnConfig;
  /** P3-E2 PR E2-1 — see `DataTablePeek`. */
  peek?: DataTablePeek<Row> | undefined;
  /** P3-E2 PR E2-1 — WRAP BEFORE WIDEN (design-system §3.3). From 769 px up
   * every header label and every chip in a cell may take a second line, so
   * the table's narrowest width is its longest WORD per column rather than
   * its longest label or chip sentence. For a book whose columns are all
   * shown by default and must fit a 1440 px viewport in every locale. The
   * phone collapse is untouched. */
  fit?: boolean;
  /**
   * Rev-7 (D-426): pages a list the screen holds in full, for a read that
   * has no server paging. Above `pageSize` rows (default 50) the table shows
   * one page at a time with the usual footer. A list that can grow past 50
   * rows pages, by `pager` or by this, and never renders every row at once.
   */
  clientPager?: DataTableClientPager | undefined;
  "data-testid"?: string;
}

/** Rev-7: the words of a client-side pager, all translated by the caller. */
export interface DataTableClientPager {
  /** Rows per page; 50 when omitted. */
  pageSize?: number | undefined;
  /**
   * The screen's filter, sort or search as one string. The table goes back
   * to page 1 whenever it changes, or whenever the row count does, so a
   * reader on page 3 who narrows the list sees its first page rather than
   * page 3 of something else. Select-all covers the page shown, as it covers
   * the loaded page under a server `pager`.
   */
  resetKey?: string | undefined;
  /**
   * D-560: a list whose rows are added at its end in place: a grid the
   * reader edits (a price list's items, a quote's lines) or a timeline read
   * further back. A change in the row count keeps the page instead of going
   * back to page 1, and rows added open the last page, where they landed.
   * `resetKey` still starts the list again at page 1.
   */
  appendable?: boolean | undefined;
  /** "1–50 of 1,081" for the page shown. */
  range: (from: number, to: number, total: number) => string;
  prevLabel: string;
  nextLabel: string;
}

/** The page size a list pages at when it gives none (rev-7). */
export const CLIENT_PAGE_SIZE = 50;

/** The most numbered slots the footer draws, gaps included (2026-10-18). */
export const PAGER_MAX_SLOTS = 7;

/**
 * The numbered slots of a pager footer: every page up to seven pages, and
 * above that the first page, the last, the current page and its neighbours,
 * with a gap wherever pages are left out (2026-10-18). The footer used to draw
 * one button per page, so a nine-page list at 412 px drew eleven 32 px
 * buttons that do not fit a phone and scrolled the whole page sideways
 * (W-SWEEP, /quality/analytics, staging 7eb5c956). At most seven slots and two
 * arrows fit the narrowest phone with room to spare, at any page count.
 */
export function pagerSlots(page: number, pageCount: number): (number | "gap")[] {
  const count = Math.max(1, Math.trunc(pageCount));
  if (count <= PAGER_MAX_SLOTS) return Array.from({ length: count }, (_, i) => i + 1);
  const current = Math.min(Math.max(Math.trunc(page), 1), count);
  const run = (from: number, to: number) =>
    Array.from({ length: to - from + 1 }, (_, i) => from + i);
  // Near either end the run widens, so there are always seven slots, the
  // arrows do not move as the reader pages, and no gap stands for one page.
  if (current <= 4) return [...run(1, 5), "gap", count];
  if (current >= count - 3) return [1, "gap", ...run(count - 4, count)];
  return [1, "gap", current - 1, current, current + 1, "gap", count];
}

/**
 * The resize handle on a resizable header (D-305 ruling 9): a focusable
 * `role="separator"` that a pointer drags, ArrowLeft and ArrowRight move by
 * 16 px and Home resets. A column with no width yet starts from the width it
 * renders at. It reports widths and holds none, so the screen's state stays
 * the one record of the layout.
 *
 * `aria-valuenow` is the configured width, and for a column with none it is
 * the width the header renders at, measured when the handle takes focus or is
 * reset. The measure runs in those event handlers and never in an effect, so
 * the first render stays the final one (design-system §5): a separator that
 * nobody has focused carries no measured value, and nothing else moves.
 */
function ColumnResizer({
  label,
  width,
  onResize,
}: {
  label: string;
  width: number | undefined;
  onResize: (width: number | undefined) => void;
}) {
  const drag = useRef<{ x: number; from: number } | null>(null);
  const [measured, setMeasured] = useState<number | undefined>(undefined);
  const headerWidth = (handle: HTMLElement): number =>
    handle.closest("th")?.getBoundingClientRect().width ?? COLUMN_WIDTH_MIN;
  const renderedWidth = (handle: HTMLElement): number => width ?? headerWidth(handle);
  const endDrag = (e: PointerEvent<HTMLSpanElement>): void => {
    if (drag.current === null) return;
    drag.current = null;
    const handle = e.currentTarget;
    if ("releasePointerCapture" in handle && handle.hasPointerCapture(e.pointerId)) {
      handle.releasePointerCapture(e.pointerId);
    }
  };
  return (
    <span
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuemin={COLUMN_WIDTH_MIN}
      aria-valuemax={COLUMN_WIDTH_MAX}
      aria-valuenow={width ?? measured}
      tabIndex={0}
      className="ds-col-rs"
      onFocus={(e) => {
        setMeasured(clampWidth(headerWidth(e.currentTarget)));
      }}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
          e.preventDefault();
          const step = e.key === "ArrowRight" ? COLUMN_RESIZE_STEP : -COLUMN_RESIZE_STEP;
          onResize(clampWidth(renderedWidth(e.currentTarget) + step));
        } else if (e.key === "Home") {
          e.preventDefault();
          onResize(undefined);
          // The column falls back to its automatic width once the screen
          // re-renders, so it is measured on the next frame, not now.
          const handle = e.currentTarget;
          requestAnimationFrame(() => {
            if (handle.isConnected) setMeasured(clampWidth(headerWidth(handle)));
          });
        }
      }}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        // No text selection while dragging, and the header never sees it.
        e.preventDefault();
        e.stopPropagation();
        const handle = e.currentTarget;
        drag.current = { x: e.clientX, from: renderedWidth(handle) };
        if ("setPointerCapture" in handle) handle.setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        const from = drag.current;
        if (from === null) return;
        onResize(clampWidth(from.from + e.clientX - from.x));
      }}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onClick={(e) => {
        e.stopPropagation();
      }}
    />
  );
}

/**
 * A header that carries a resize separator. The separator is a named control
 * inside the `<th>`, and a header's name is otherwise computed from all of its
 * content, so "Total" would read as "Total Resize Total" to a screen reader
 * and to every exact-name `columnheader` locator. The header's own content
 * therefore sits in a span that names the `<th>` through `aria-labelledby`,
 * and the header reads exactly as it does without the separator. The id comes
 * from `useId` in this component alone, so a table without a separator keeps
 * its tree, and its ids, unchanged.
 */
function ResizableHeader({
  ariaSort,
  className,
  resizeLabel,
  width,
  onResize,
  children,
}: {
  ariaSort: "ascending" | "descending" | undefined;
  className: string;
  resizeLabel: string;
  width: number | undefined;
  onResize: (width: number | undefined) => void;
  children: ReactNode;
}) {
  const labelId = useId();
  return (
    <th scope="col" aria-sort={ariaSort} className={className} aria-labelledby={labelId}>
      <span id={labelId}>{children}</span>
      <ColumnResizer label={resizeLabel} width={width} onResize={onResize} />
    </th>
  );
}

/** The ONE caret (1b): inline stroke glyph, `currentColor` from the th, only
 * ever rendered on the active column. */
function SortCaret({ dir }: { dir: "ascending" | "descending" }) {
  return (
    <svg
      className="ds-sort-caret"
      viewBox="0 0 16 16"
      aria-hidden="true"
      focusable="false"
      data-dir={dir}
    >
      {dir === "ascending" ? <path d="M4 10l4-4 4 4" /> : <path d="M4 6l4 4 4-4" />}
    </svg>
  );
}

/**
 * WAVE 1b's validateSearch half, shared so every adopting screen states the
 * same rule: keep `order` only when it is a member of the endpoint's generated
 * enum (D-142 — never a free string); anything else, including absence, means
 * the endpoint's declared default. Usage:
 *
 *   validateSearch: (s) => ({ order: orderSearchParam(CustomersOrderValues)(s.order) })
 */
export function orderSearchParam<O extends string>(
  valid: readonly O[],
): (raw: unknown) => O | undefined {
  return (raw: unknown): O | undefined =>
    typeof raw === "string" && (valid as readonly string[]).includes(raw) ? (raw as O) : undefined;
}

export function DataTable<Row, K extends string = string>({
  columns,
  rows: allRows,
  rowKey,
  empty,
  error,
  loading = false,
  loadingLabel,
  pager,
  caption,
  onRowClick,
  rowTestId = "ds-row",
  rowClassName,
  rowProps,
  expansion,
  expansionLabel,
  sort,
  selectable,
  bulkBar,
  keyboardNav = false,
  sticky = false,
  columnConfig,
  peek,
  fit = false,
  clientPager,
  ...rest
}: DataTableProps<Row, K>) {
  // Hooks stay above the early returns (rules of hooks): the 1d anchor is the
  // index of the ONE row at tabIndex 0, clamped to the loaded rows at render
  // so it survives a shrinking page without an effect.
  const [anchorRaw, setAnchorRaw] = useState(0);
  // Rev-7: the client pager's page, clamped at render like the anchor.
  const [clientPageRaw, setClientPage] = useState(1);
  // A new list, by count or by the caller's filter key, starts at page 1.
  // Adjusted during render, React's documented pattern for state derived
  // from props, so the stale page is never painted.
  // An appendable list (D-560) keys on its filter alone: a row added or
  // removed keeps the reader where they are, and rows added open the last page.
  const appendable = clientPager?.appendable === true;
  const clientSignature = appendable
    ? `\u0000${clientPager.resetKey ?? ""}`
    : `${String(allRows.length)}\u0000${clientPager?.resetKey ?? ""}`;
  const [clientSignatureSeen, setClientSignatureSeen] = useState(clientSignature);
  if (clientSignatureSeen !== clientSignature) {
    setClientSignatureSeen(clientSignature);
    setClientPage(1);
  }
  const [clientCountSeen, setClientCountSeen] = useState(allRows.length);
  if (clientCountSeen !== allRows.length) {
    setClientCountSeen(allRows.length);
    if (appendable && allRows.length > clientCountSeen) {
      const size = Math.max(1, Math.trunc(clientPager.pageSize ?? CLIENT_PAGE_SIZE));
      setClientPage(Math.max(1, Math.ceil(allRows.length / size)));
    }
  }
  const rowEls = useRef<(HTMLTableRowElement | null)[]>([]);
  // The peek and the cell editors are offered from 769 px up only (D-314
  // ruling 4, D-326 ruling 6). The width is read at mount, so the first render
  // is already the final one (§5).
  const desktop = useDesktop();

  // Runtime guards — misuse is a build defect, said loudly at the seam. They
  // read the screen's OWN declarations only: a stored column configuration
  // never throws (D-305 ruling 9), because a shared view reaches every reader
  // of the screen and a throw would break the list for all of them.
  columns.forEach((c, index) => {
    if (c.sortKey !== undefined && c.headerNode !== undefined) {
      throw new Error(
        `DataTable: column "${c.key}" declares both sortKey and headerNode — ` +
          "a control header cannot be a sort toggle (§3.3 wave 1b)",
      );
    }
    if ((selectable !== undefined || columnConfig !== undefined) && c.key === SEL_KEY) {
      throw new Error(
        `DataTable: column key "${SEL_KEY}" is reserved for the internal selection column`,
      );
    }
    if (c.editable !== undefined && c.render !== undefined) {
      throw new Error(
        `DataTable: column "${c.key}" declares both editable and render — an editable ` +
          "column's value is drawn by editable.display (§3.3, D-326)",
      );
    }
    if (columnConfig !== undefined && c.actions === true && index !== columns.length - 1) {
      throw new Error(
        `DataTable: actions column "${c.key}" must be declared last — with columnConfig ` +
          "it is the pinned trail (§3.3, D-305 ruling 9)",
      );
    }
  });

  if (error !== undefined && error !== null) return <>{error}</>;
  if (loading) {
    return (
      <TableSkeleton
        rows={6}
        cols={columns.length + (selectable !== undefined ? 1 : 0)}
        label={loadingLabel ?? ""}
      />
    );
  }
  if (allRows.length === 0) return <>{empty}</>;

  // ---- rev-7: the client pager ---------------------------------------------
  // A list above the page size shows one page; everything below reads `rows`
  // as the page, so selection, the anchor and the keys see what is shown.
  const clientSize = Math.max(1, Math.trunc(clientPager?.pageSize ?? CLIENT_PAGE_SIZE));
  const clientPages =
    clientPager !== undefined && pager === undefined ? Math.ceil(allRows.length / clientSize) : 1;
  const clientPage = Math.min(Math.max(clientPageRaw, 1), clientPages);
  const rows =
    clientPages > 1
      ? allRows.slice((clientPage - 1) * clientSize, clientPage * clientSize)
      : allRows;
  const footer: DataTablePager | undefined =
    pager ??
    (clientPager !== undefined && clientPages > 1
      ? {
          range: clientPager.range(
            (clientPage - 1) * clientSize + 1,
            Math.min(clientPage * clientSize, allRows.length),
            allRows.length,
          ),
          page: clientPage,
          pageCount: clientPages,
          onPage: (p: number) => {
            setClientPage(p);
          },
          prevLabel: clientPager.prevLabel,
          nextLabel: clientPager.nextLabel,
        }
      : undefined);

  // ---- PR-E4: which columns render, in what order, at what width ----------
  // A caller passing none of the configuration inputs takes the declared
  // columns untouched, which is what keeps every non-adopting screen's markup
  // byte-identical.
  const layout: readonly ResolvedColumn<Row, K>[] | undefined =
    columnConfig !== undefined ||
    columns.some((c) => c.presence !== undefined || c.defaultHidden === true)
      ? resolveColumns(columns, rows, columnConfig?.value ?? [])
      : undefined;
  const shown: readonly DataTableColumn<Row, K>[] =
    layout === undefined ? columns : layout.map((c) => c.column);
  const widths = new Map<string, number>();
  const trailKeys = new Set<string>();
  for (const c of layout ?? []) {
    if (c.width !== undefined) widths.set(c.key, c.width);
    if (c.pin === "trail") trailKeys.add(c.key);
  }
  const sized = widths.size > 0;
  const onResize = columnConfig?.onResize;
  // Every rendered column resizes except `_sel` and the `actions` trail; the
  // pinned lead does, since a pin fixes only a column's place.
  const resizable = (c: DataTableColumn<Row, K>): boolean =>
    onResize !== undefined && c.key !== SEL_KEY && !trailKeys.has(c.key);

  // ---- 1c: the internal selection column (prepended, key "_sel") ----------
  const selTestId = selectable?.testId ?? "ds-sel";
  const selectableKeys =
    selectable === undefined
      ? []
      : rows.filter((row) => selectable.isRowSelectable?.(row) ?? true).map((row) => rowKey(row));
  const selectedCount =
    selectable === undefined
      ? 0
      : selectableKeys.filter((key) => selectable.selected.has(key)).length;
  // Select-all covers ONLY the loaded selectable rows — never "all N matching".
  const allSelected =
    selectable !== undefined &&
    selectableKeys.length > 0 &&
    selectedCount === selectableKeys.length;

  const toggleKey = (key: string): void => {
    if (selectable === undefined) return;
    const next = new Set(selectable.selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    selectable.onChange(next);
  };

  const allColumns: readonly DataTableColumn<Row, K>[] =
    selectable === undefined
      ? shown
      : [
          {
            key: SEL_KEY,
            header: selectable.headerLabel,
            // The §3.3 headerNode seam carries the select-all control.
            headerNode: (
              <input
                type="checkbox"
                aria-label={selectable.headerLabel}
                data-testid={`${selTestId}-all`}
                checked={allSelected}
                ref={(el) => {
                  if (el !== null) el.indeterminate = !allSelected && selectedCount > 0;
                }}
                onChange={() => {
                  selectable.onChange(allSelected ? new Set() : new Set(selectableKeys));
                }}
              />
            ),
            render: (row: Row) => (
              <input
                type="checkbox"
                aria-label={selectable.rowLabel(row)}
                data-testid={selTestId}
                checked={selectable.selected.has(rowKey(row))}
                disabled={!(selectable.isRowSelectable?.(row) ?? true)}
                onChange={() => {
                  toggleKey(rowKey(row));
                }}
              />
            ),
          },
          ...shown,
        ];

  // The collapse contract with a selection column: the card subject is the
  // first NON-SELECTION cell and stays unlabelled; labels start after it.
  const labelFloor = selectable === undefined ? 0 : 1;

  // ---- 1b: parse the active order token once ------------------------------
  const sortDesc = sort?.value.startsWith("-") ?? false;
  const sortActiveKey =
    sort === undefined ? undefined : sortDesc ? sort.value.slice(1) : (sort.value as string);

  // ---- E2-1: the peek -------------------------------------------------------
  // A row's button, and Shift+Enter on the row, open its peek, or close it
  // when it is the one open. Nothing here navigates.
  const peekOffered = peek !== undefined && desktop;
  // Rev-7: a row with no action of its own opens its peek on a click.
  const rowPeeks = peekOffered && onRowClick === undefined;
  // Rev-7: the columns a phone card leaves out when the table prioritises.
  const phoneHidden = desktop ? undefined : phoneHiddenKeys(shown);
  const togglePeek = (row: Row, code: string): void => {
    if (peek === undefined) return;
    if (peek.current === code) peek.onClose?.();
    else peek.onOpen(code, row);
  };

  // ---- 1d: the roving-tabindex anchor -------------------------------------
  const anchor = Math.min(Math.max(anchorRaw, 0), rows.length - 1);
  const rowKeyDown = !keyboardNav
    ? undefined
    : (row: Row, rowIndex: number) => (e: KeyboardEvent<HTMLTableRowElement>) => {
        // A control inside the row (checkbox, link) owns its own keys —
        // acting on bubbled events would double-toggle the checkbox.
        if (e.target !== e.currentTarget) return;
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          e.preventDefault();
          const next =
            e.key === "ArrowDown"
              ? Math.min(rowIndex + 1, rows.length - 1)
              : Math.max(rowIndex - 1, 0);
          setAnchorRaw(next);
          rowEls.current[next]?.focus();
        } else if (e.key === "Enter") {
          const code = peekOffered && e.shiftKey ? peek.key(row) : undefined;
          if (code !== undefined) {
            e.preventDefault();
            togglePeek(row, code);
            return;
          }
          onRowClick?.(row);
        } else if (e.key === " " && selectable !== undefined) {
          // preventDefault so the page never scrolls under the selection.
          e.preventDefault();
          if (selectable.isRowSelectable?.(row) ?? true) toggleKey(rowKey(row));
        }
      };

  const wrapClass = sticky ? "ds-tbl-wrap ds-tbl-wrap--pane" : "ds-tbl-wrap";
  const tblClass = [
    "ds-tbl",
    sticky ? "ds-tbl--frozen" : undefined,
    selectable !== undefined ? "ds-tbl--sel" : undefined,
    sized ? "ds-tbl--sized" : undefined,
    fit ? "ds-tbl--fit" : undefined,
  ]
    .filter((x): x is string => x !== undefined)
    .join(" ");

  return (
    <>
      {selectable !== undefined && bulkBar !== undefined && selectable.selected.size > 0 ? (
        <div className="ds-bulkbar" data-testid="ds-bulkbar">
          <span className="ds-bulkbar-count">{bulkBar.countLabel}</span>
          <div className="ds-bulkbar-verbs">{bulkBar.children}</div>
          <button type="button" className="ds-lnk ds-bulkbar-clear" onClick={bulkBar.onClear}>
            {bulkBar.clearLabel}
          </button>
        </div>
      ) : null}
      <div className={wrapClass}>
        <table className={tblClass} {...rest}>
          {caption !== undefined ? <caption className="visually-hidden">{caption}</caption> : null}
          {sized ? (
            // The widths take effect only from 769 px up (styles/office.css):
            // each `<col>` carries its width as a custom property, and only
            // the desktop block reads it, so a phone renders no widths.
            <colgroup>
              {allColumns.map((c) => {
                const width = widths.get(c.key);
                return (
                  <col
                    key={c.key}
                    style={
                      width === undefined
                        ? undefined
                        : ({ "--ds-col-w": `${String(width)}px` } as CSSProperties)
                    }
                  />
                );
              })}
            </colgroup>
          ) : null}
          <thead>
            <tr>
              {allColumns.map((c) => {
                const isSortable = sort !== undefined && c.sortKey !== undefined;
                const isActive = isSortable && sortActiveKey === c.sortKey;
                const activeDir: "ascending" | "descending" | undefined = isActive
                  ? sortDesc
                    ? "descending"
                    : "ascending"
                  : undefined;
                const headClass =
                  c.key === SEL_KEY
                    ? "ds-td-sel"
                    : c.numeric === true || c.kind === "money" || c.kind === "qty"
                      ? "ds-td-num"
                      : c.actions
                        ? "ds-td-acts"
                        : undefined;
                const content =
                  sort !== undefined && c.sortKey !== undefined
                    ? (() => {
                        const key = c.sortKey;
                        // Inactive → its sortFirst direction; active → flip.
                        // No third "unsorted" state: the endpoint always has
                        // an order (D-242).
                        const nextDir: "ascending" | "descending" = isActive
                          ? sortDesc
                            ? "ascending"
                            : "descending"
                          : c.sortFirst === "desc"
                            ? "descending"
                            : "ascending";
                        return (
                          <button
                            type="button"
                            className="ds-sort"
                            aria-label={sort.toggleLabel(c.header, nextDir)}
                            onClick={() => {
                              sort.onChange(nextDir === "descending" ? `-${key}` : key);
                            }}
                          >
                            {c.header}
                            {activeDir !== undefined ? <SortCaret dir={activeDir} /> : null}
                          </button>
                        );
                      })()
                    : (c.headerNode ?? c.header);
                if (resizable(c) && onResize !== undefined && columnConfig !== undefined) {
                  return (
                    <ResizableHeader
                      key={c.key}
                      ariaSort={activeDir}
                      className={headClass === undefined ? "ds-th-rs" : `${headClass} ds-th-rs`}
                      resizeLabel={columnConfig.resizeLabel(c.header)}
                      width={widths.get(c.key)}
                      onResize={(width) => {
                        onResize(c.key, width);
                      }}
                    >
                      {content}
                    </ResizableHeader>
                  );
                }
                return (
                  <th
                    key={c.key}
                    scope="col"
                    // aria-sort on the ACTIVE th only (1b).
                    aria-sort={activeDir}
                    className={headClass}
                  >
                    {content}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => {
              // Evaluated ONCE per row: a caller's expansion callback may build
              // a form, and calling it twice (test + render) would mint two.
              const panel = expansion?.(row);
              // The row's peek, and whether it is the one open. A table
              // without `peek` computes nothing here and marks nothing.
              const peekKey = peek?.key(row);
              const peeked = peekKey !== undefined && peek?.current === peekKey;
              const ownClass = rowClassName?.(row);
              const rowClass = !peeked
                ? ownClass
                : ownClass === undefined || ownClass === ""
                  ? "is-peeked"
                  : `${ownClass} is-peeked`;
              return (
                <Fragment key={rowKey(row)}>
                  <tr
                    data-testid={rowTestId}
                    className={rowClass}
                    data-peek-row={peekKey}
                    tabIndex={keyboardNav ? (rowIndex === anchor ? 0 : -1) : undefined}
                    ref={
                      keyboardNav
                        ? (el) => {
                            rowEls.current[rowIndex] = el;
                          }
                        : undefined
                    }
                    onKeyDown={rowKeyDown?.(row, rowIndex)}
                    onClick={
                      onRowClick !== undefined || keyboardNav || rowPeeks
                        ? (e) => {
                            if (keyboardNav) setAnchorRaw(rowIndex);
                            if (onRowClick !== undefined) {
                              onRowClick(row);
                              return;
                            }
                            // Rev-7: with no row action of its own, the row is
                            // the peek's target, and a control inside it keeps
                            // its own meaning.
                            if (
                              rowPeeks &&
                              peekKey !== undefined &&
                              !fromControl(e.target, e.currentTarget)
                            ) {
                              togglePeek(row, peekKey);
                            }
                          }
                        : undefined
                    }
                    {...(rowProps?.(row, rowIndex) ?? {})}
                  >
                    {allColumns.map((c, i) => {
                      const kinds = kindClasses(c.kind);
                      const classes = [
                        c.key === SEL_KEY ? "ds-td-sel" : undefined,
                        c.numeric && !kinds.includes("ds-td-num") ? "ds-td-num" : undefined,
                        c.actions ? "ds-td-acts" : undefined,
                        c.id && !kinds.includes("ds-td-id") ? "ds-td-id" : undefined,
                        c.age ? "ds-td-age" : undefined,
                        ...kinds,
                        // Rev-7: the lead cell carries the peek's icon.
                        peekOffered && peekKey !== undefined && i === labelFloor
                          ? "ds-td-peek"
                          : undefined,
                      ].filter((x): x is string => x !== undefined);
                      const raw = (row as Record<string, unknown>)[c.key];
                      const content =
                        c.editable !== undefined ? (
                          // E2-2d: the editor from 769 px up, the value alone
                          // below, where a phone edits on the record's page.
                          desktop ? (
                            <CellEditor row={row} editor={c.editable} />
                          ) : (
                            cellEditableValue(c.editable, row)
                          )
                        ) : c.render !== undefined ? (
                          c.render(row)
                        ) : typeof raw === "string" || typeof raw === "number" ? (
                          raw
                        ) : null;
                      return (
                        <td
                          key={c.key}
                          className={classes.length > 0 ? classes.join(" ") : undefined}
                          // The collapse contract: every cell after the first
                          // non-selection cell carries its label so the phone
                          // card names each value; the card subject (and the
                          // control-only selection cell) stay unlabelled.
                          data-label={i > labelFloor ? (c.label ?? c.header) : undefined}
                          // Rev-7, phone cards only: a cell with no value, and
                          // a column the card leaves to the record's page, are
                          // not printed. The desktop markup is unchanged.
                          data-empty={
                            !desktop && i > labelFloor && isEmptyCellContent(content)
                              ? ""
                              : undefined
                          }
                          data-phone-hidden={
                            !desktop && i > labelFloor && phoneHidden?.has(c.key) === true
                              ? ""
                              : undefined
                          }
                          // The row checkbox must never open the row (1c).
                          onClick={
                            c.key === SEL_KEY
                              ? (e) => {
                                  e.stopPropagation();
                                }
                              : undefined
                          }
                        >
                          {content}
                          {peekOffered && peekKey !== undefined && i === labelFloor ? (
                            // After the lead cell's content (design-system §3.3).
                            <button
                              type="button"
                              className="ds-peek-btn"
                              aria-label={peek.rowLabel?.(row) ?? peek.label}
                              aria-expanded={peeked}
                              aria-controls={peeked ? SIDE_PEEK_ID : undefined}
                              data-peek-key={peekKey}
                              data-testid={peek.testId ?? "ds-peek"}
                              onClick={(e) => {
                                // The row's own click keeps its meaning, so
                                // this one never reaches it.
                                e.stopPropagation();
                                // Safari does not focus a button on click; the
                                // peek gives focus back to its opener, so the
                                // opener has to hold it.
                                e.currentTarget.focus();
                                togglePeek(row, peekKey);
                              }}
                            >
                              <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                                <path d="M1.8 8s2.3-4.2 6.2-4.2S14.2 8 14.2 8s-2.3 4.2-6.2 4.2S1.8 8 1.8 8z" />
                                <circle cx="8" cy="8" r="1.9" />
                              </svg>
                              <span className="visually-hidden">{peek.label}</span>
                            </button>
                          ) : null}
                        </td>
                      );
                    })}
                  </tr>
                  {panel !== undefined && panel !== null && panel !== false ? (
                    <tr className="ds-tbl-exp" data-testid={`${rowTestId}-expansion`}>
                      <td colSpan={allColumns.length} data-label={expansionLabel?.(row)}>
                        {panel}
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {footer !== undefined ? (
        <div className="ds-tfoot">
          <span className="num">{footer.range}</span>
          {footer.meta !== undefined ? (
            <>
              <span aria-hidden="true">·</span>
              <span>{footer.meta}</span>
            </>
          ) : null}
          <nav className="ds-pgr" aria-label={footer.range}>
            <button
              type="button"
              aria-label={footer.prevLabel}
              disabled={footer.hasPrev !== undefined ? !footer.hasPrev : footer.page <= 1}
              onClick={() => {
                footer.onPage(footer.page - 1);
              }}
            >
              ‹
            </button>
            {footer.pageCount !== undefined
              ? pagerSlots(footer.page, footer.pageCount).map((p, i) =>
                  p === "gap" ? (
                    <span key={`gap-${String(i)}`} className="ds-pgr-gap" aria-hidden="true">
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      aria-current={p === footer.page ? "true" : undefined}
                      onClick={() => {
                        footer.onPage(p);
                      }}
                      className="num"
                    >
                      {p}
                    </button>
                  ),
                )
              : null}
            <button
              type="button"
              aria-label={footer.nextLabel}
              disabled={
                footer.hasNext !== undefined
                  ? !footer.hasNext
                  : footer.pageCount !== undefined && footer.page >= footer.pageCount
              }
              onClick={() => {
                footer.onPage(footer.page + 1);
              }}
            >
              ›
            </button>
          </nav>
        </div>
      ) : null}
    </>
  );
}
